import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Linking,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle as SvgCircle } from 'react-native-svg';
import * as DocumentPicker from 'expo-document-picker';
import { FONTS, API_BASE_HOST } from '../../constants/config';
import { PageContainer, Breadcrumb } from '../../components';
import CancelConfirmModal from '../../components/CancelConfirmModal';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { kpisApi, kpiValuesApi, schedulesApi, evidenceApi } from '../../services/apiService';
import { KPI, KPIValue, KPIReadingScheduleList } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { periodTargetForDate } from '../../utils/targetGrid';
import { useResponsive, useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { useTranslation } from 'react-i18next';
import { normalizeArabicNumbers } from '../../utils/rtl';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
type RouteProps = RouteProp<MainStackParamList, 'AddKPIReading'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icons
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 20, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const InfoIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeWidth="1.5"/>
    <Path d="M12 16V12M12 8H12.01" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </Svg>
);

const EyeIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Progress Circle Component
const ProgressCircle = ({ percentage, readings, total }: { percentage: number; readings: number; total: number }) => {
  const size = 64;
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min(readings / total, 1);
  const strokeDashoffset = circumference - progress * circumference;

  return (
    <View style={progressStyles.container}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <SvgCircle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={tokens.colors.raw.gray["200"]}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <SvgCircle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={tokens.colors.raw.green["500"]}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
    </View>
  );
};

const getProgressStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const progressStyles = getProgressStyles(true);

// ============================================================
// Helpers
// ============================================================

const getExpectedReadingsPerYear = (frequency: string | undefined): number => {
  const frequencyMap: Record<string, number> = {
    daily: 365,
    weekly: 52,
    monthly: 12,
    quarterly: 4,
    half_yearly: 2,
    yearly: 1,
  };
  return frequencyMap[frequency || ''] || 0;
};

/**
 * Get the start and end dates of the period containing `date` based on KPI frequency.
 * Used to check if a reading already exists for the same period.
 */
const getPeriodBoundaries = (dateStr: string, frequency: string): { start: string; end: string } => {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-indexed

  // Use local date parts to avoid UTC timezone shift (toISOString shifts dates in UTC+ zones)
  const fmt = (dt: Date) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  switch (frequency) {
    case 'daily':
      return { start: dateStr, end: dateStr };
    case 'weekly': {
      const day = d.getDay(); // 0=Sun
      const monday = new Date(d);
      monday.setDate(d.getDate() - ((day + 6) % 7)); // Go back to Monday
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { start: fmt(monday), end: fmt(sunday) };
    }
    case 'monthly': {
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0); // Last day of month
      return { start: fmt(start), end: fmt(end) };
    }
    case 'quarterly': {
      const q = Math.floor(month / 3);
      const start = new Date(year, q * 3, 1);
      const end = new Date(year, q * 3 + 3, 0);
      return { start: fmt(start), end: fmt(end) };
    }
    case 'half_yearly': {
      if (month < 6) {
        return { start: `${year}-01-01`, end: `${year}-06-30` };
      }
      return { start: `${year}-07-01`, end: `${year}-12-31` };
    }
    case 'yearly':
      return { start: `${year}-01-01`, end: `${year}-12-31` };
    default:
      return { start: dateStr, end: dateStr };
  }
};

/**
 * Returns the window during which a reading can be entered for a scheduled period.
 * For longer frequencies, the window is the last month of the period.
 * - daily/weekly/monthly: same as period boundaries (full period)
 * - quarterly: last month of the quarter
 * - half_yearly: last month of the half
 * - yearly: last month of the year (December)
 */
const getReadingWindow = (dateStr: string, frequency: string): { start: string; end: string } => {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-indexed

  const fmt = (dt: Date) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // For quarterly, half_yearly, and yearly: the reading window is the
  // month of the scheduled_date (supports both calendar and academic year schedules).
  switch (frequency) {
    case 'quarterly':
    case 'half_yearly':
    case 'yearly': {
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0);
      return { start: fmt(start), end: fmt(end) };
    }
    default:
      return getPeriodBoundaries(dateStr, frequency);
  }
};

const hasReadingInPeriod = (readings: KPIValue[], dateStr: string, frequency: string): boolean => {
  if (!dateStr || !frequency) return false;
  const { start, end } = getPeriodBoundaries(dateStr, frequency);
  return readings.some(r => r.measurement_date >= start && r.measurement_date <= end);
};

/**
 * Check whether a chosen manual-entry date falls inside the allowed reading window
 * for the KPI's frequency. For quarterly/half_yearly/yearly, the window is the last
 * month of the period (e.g., yearly = December). For daily/weekly/monthly the
 * window equals the full period, so this always returns true.
 */
const isDateWithinReadingWindow = (dateStr: string, frequency: string): boolean => {
  if (!dateStr || !frequency) return true;
  const { start, end } = getReadingWindow(dateStr, frequency);
  return dateStr >= start && dateStr <= end;
};

const getPeriodLabel = (frequency: string): string => {
  const labels: Record<string, string> = {
    daily: 'اليوم',
    weekly: 'هذا الأسبوع',
    monthly: 'هذا الشهر',
    quarterly: 'هذا الربع',
    half_yearly: 'هذا النصف من السنة',
    yearly: 'هذه السنة',
  };
  return labels[frequency] || 'هذه الفترة';
};

// ============================================================
// Main Component
// ============================================================

export default function AddKPIReadingScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { kpiId, readingId } = route.params;
  const isEditMode = !!readingId;
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { translate, translateOrg } = useAutoTranslate();
  const { navigateToHome } = useRoleNavigation();
  const { isMobile, isTablet, isDesktop, isSmallMobile, width, containerPadding, cardPadding, sectionSpacing } = useResponsive();

  // Theme colors for form elements
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : tokens.colors.raw.gray["100"],
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    iconColor: colors.textMuted,
    infoMessage: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    radioBorder: isDark ? '#4B5563' : tokens.colors.raw.gray["300"],
    readingBg: isDark ? colors.backgroundAlt : tokens.colors.raw.gray["50"],
    badgeBg: isDark ? 'rgba(100, 116, 139, 0.2)' : 'rgba(51, 65, 85, 0.1)',
  };

  // Check if user is strategy_team (they can't add readings)
  const isStrategyTeam = user?.profile?.user_type === 'strategy_team' || user?.profile?.user_type === 'superuser';

  // Redirect strategy_team users - they don't have permission to add readings
  useEffect(() => {
    if (isStrategyTeam) {
      showError(t('readings.noPermissionToAddReadings'));
      navigation.goBack();
    }
  }, [isStrategyTeam, navigation, showError, t]);

  // Responsive breakpoints
  const isSmallScreen = isMobile;

  const [kpi, setKpi] = useState<KPI | null>(null);
  const [recentReadings, setRecentReadings] = useState<KPIValue[]>([]);
  const [allReadings, setAllReadings] = useState<KPIValue[]>([]);
  const [currentYearReadingsCount, setCurrentYearReadingsCount] = useState(0);
  const [lateSchedules, setLateSchedules] = useState<KPIReadingScheduleList[]>([]);
  const [pendingSchedules, setPendingSchedules] = useState<KPIReadingScheduleList[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form state
  const [selectedPeriod, setSelectedPeriod] = useState<'late' | 'scheduled' | 'manual'>('scheduled');
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);
  const [evidenceType, setEvidenceType] = useState<'file' | 'text'>('file');
  const [selectedFile, setSelectedFile] = useState<DocumentPicker.DocumentPickerAsset | null>(null);
  // In edit mode, the reading's already-attached evidence (loaded from the API).
  // While it's present and not being replaced, the form shows it read-only and
  // the required-evidence check is satisfied without re-attaching.
  const [existingEvidence, setExistingEvidence] = useState<any | null>(null);
  const [replaceEvidence, setReplaceEvidence] = useState(false);
  const [formData, setFormData] = useState({
    value: '',
    measurementDate: new Date().toISOString().split('T')[0],
    notes: '',
    evidenceText: '',
  });

  useUnsavedChangesWarning(!!formData.value || !!formData.notes);

  // File picker handler
  const handleFilePick = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/*', 'application/pdf', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/zip', 'application/x-zip-compressed'],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        setSelectedFile(result.assets[0]);
        if (errors.evidence) {
          setErrors(prev => ({ ...prev, evidence: '' }));
        }
      }
    } catch (error) {
      console.error('Error picking file:', error);
      showError(t('readings.filePickFailed'));
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      await fetchData();
    };
    load();
    return () => { cancelled = true; };
  }, [kpiId, readingId]);

  const fetchData = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const promises: Promise<any>[] = [
        kpisApi.get(kpiId),
        kpisApi.getReadings(kpiId, { limit: 3 }),
        kpisApi.getSchedules(kpiId),
        kpisApi.getReadings(kpiId, { start_date: `${currentYear - 1}-01-01`, end_date: `${currentYear}-12-31` }),
      ];
      if (isEditMode) {
        promises.push(kpiValuesApi.get(readingId));
      }
      let [kpiData, readingsData, schedulesData, yearReadingsData, existingReading] = await Promise.all(promises);
      setKpi(kpiData);
      setRecentReadings(Array.isArray(readingsData) ? readingsData.slice(0, 3) : []);
      setAllReadings(Array.isArray(yearReadingsData) ? yearReadingsData : []);
      setCurrentYearReadingsCount(Array.isArray(yearReadingsData) ? yearReadingsData.length : 0);

      // Auto-generate schedules if none exist
      if (!Array.isArray(schedulesData) || schedulesData.length === 0) {
        try {
          await schedulesApi.generate(kpiId, new Date().getFullYear());
          schedulesData = await kpisApi.getSchedules(kpiId);
        } catch (e) {
          console.warn('Failed to auto-generate schedules:', e);
        }
      }

      // Pre-fill form in edit mode
      if (isEditMode && existingReading) {
        setFormData({
          value: String(existingReading.value || ''),
          measurementDate: existingReading.measurement_date || new Date().toISOString().split('T')[0],
          notes: existingReading.notes || '',
          evidenceText: '',
        });
        setSelectedPeriod('manual');

        // Load the reading's existing evidence so it's shown (not silently lost)
        // and satisfies the required-evidence check until the user replaces it.
        try {
          const ev = await evidenceApi.forObject('kpi_value', readingId);
          const first = Array.isArray(ev) ? ev[0] : null;
          setExistingEvidence(first || null);
          if (first?.evidence_type === 'text') setEvidenceType('text');
        } catch (e) {
          console.warn('Failed to load reading evidence:', e);
        }
      }

      // Separate schedules into late and current pending (only current period)
      const yearReadings: KPIValue[] = Array.isArray(yearReadingsData) ? yearReadingsData : [];
      const freq = kpiData.frequency || '';
      const todayStr = new Date().toISOString().split('T')[0];

      if (Array.isArray(schedulesData)) {
        const now = new Date();

        // Filter out schedules whose period already has a reading
        const hasNoReadingForPeriod = (s: KPIReadingScheduleList) =>
          !hasReadingInPeriod(yearReadings, s.scheduled_date, freq);

        // Lateness comes from the server (`is_overdue`), which applies the
        // per-frequency working-day grace window. Deriving it here from
        // `today > end-of-period` — as this used to — has no grace at all, so
        // a period the dashboard badges مستحق landed under "الفترات المتأخرة"
        // and demanded a lateness justification. Same rule, one place.
        const late = schedulesData
          .filter((s: KPIReadingScheduleList) => !s.is_completed && s.is_overdue)
          .filter(hasNoReadingForPeriod);

        // Open (مستحقة) periods: the period has CLOSED (scheduled_date, which
        // is the period end, has arrived) and its grace window hasn't run out.
        // Offering a period that hasn't ended yet is what let the scheduled
        // path store a future-dated reading — mark_complete now rejects those,
        // so don't put them in front of the user at all. This set is exactly
        // what the dashboard's due-readings table lists.
        const currentPeriodSchedules = schedulesData.filter(
          (s: KPIReadingScheduleList) =>
            !s.is_completed && !s.is_overdue && s.scheduled_date <= todayStr,
        ).filter(hasNoReadingForPeriod);

        // Oldest first so the most urgent open period is the default.
        const pending = [...currentPeriodSchedules].sort(
          (a, b) => a.scheduled_date.localeCompare(b.scheduled_date),
        );
        setLateSchedules(late);
        setPendingSchedules(pending);

        // Auto-select first pending schedule if available
        if (pending.length > 0) {
          setSelectedScheduleId(pending[0].id);
          setSelectedPeriod('scheduled');
        } else if (late.length > 0) {
          setSelectedScheduleId(late[0].id);
          setSelectedPeriod('late');
        } else {
          setSelectedPeriod('manual');
        }
      }
    } catch (err: any) {
      showError(t('kpis.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const isLateReading = selectedPeriod === 'late';

  // Check if a reading already exists for the selected period (manual mode)
  const periodAlreadyHasReading = !isEditMode && selectedPeriod === 'manual' && kpi
    ? hasReadingInPeriod(allReadings, formData.measurementDate, kpi.frequency)
    : false;

  // Check if manual-entry date is outside the KPI's allowed reading window
  // (e.g., yearly KPIs only accept December dates, quarterly only the last month)
  const isDateOutsideDueWindow = !isEditMode && selectedPeriod === 'manual' && kpi && formData.measurementDate
    ? !isDateWithinReadingWindow(formData.measurementDate, kpi.frequency)
    : false;

  const dueWindowRange = !isEditMode && selectedPeriod === 'manual' && kpi && formData.measurementDate
    ? getReadingWindow(formData.measurementDate, kpi.frequency)
    : null;

  // The date whose period drives the target lookup. In manual/edit mode this is
  // the user-entered measurement date; in scheduled/late mode it's the selected
  // schedule's scheduled_date (which is also the date the reading is saved
  // against), so the target box reflects the period actually being read — not
  // today's period, which is what formData.measurementDate defaults to and never
  // updates when a period radio is picked.
  const targetLookupDate = useMemo(() => {
    if (isEditMode || selectedPeriod === 'manual') return formData.measurementDate;
    const selected = [...pendingSchedules, ...lateSchedules].find(s => s.id === selectedScheduleId);
    return selected?.scheduled_date ?? formData.measurementDate;
  }, [isEditMode, selectedPeriod, selectedScheduleId, pendingSchedules, lateSchedules, formData.measurementDate]);

  // Target shown next to the achieved value = the target of THE PERIOD being
  // read (e.g. a Q1 reading on a quarterly KPI shows the Q1 grid target, 20),
  // not the aggregated annual total (40). Mirrors the backend per-period
  // achievement math. Falls back to the KPI's overall target_value for legacy /
  // gridless KPIs or dates outside the target window.
  const periodTargetValue = useMemo(
    () => periodTargetForDate(kpi?.target_values, kpi?.frequency ?? '', targetLookupDate, kpi?.target_value ?? null),
    [kpi?.target_values, kpi?.frequency, kpi?.target_value, targetLookupDate],
  );

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.value.trim()) {
      newErrors.value = t('validation.valueRequired');
    } else if (isNaN(parseFloat(formData.value))) {
      newErrors.value = t('validation.valueMustBeNumber');
    }

    if (selectedPeriod === 'manual' && !formData.measurementDate) {
      newErrors.measurementDate = t('validation.measurementDateRequired');
    } else if (periodAlreadyHasReading) {
      const label = getPeriodLabel(kpi?.frequency || '');
      newErrors.measurementDate = `توجد قراءة مسجلة بالفعل في ${label}`;
    } else if (isDateOutsideDueWindow && dueWindowRange) {
      newErrors.measurementDate = `يجب أن يكون تاريخ القراءة ضمن فترة الاستحقاق (${formatShortDate(dueWindowRange.start)} - ${formatShortDate(dueWindowRange.end)})`;
    }

    if (isLateReading && !formData.notes.trim()) {
      newErrors.notes = t('validation.notesRequiredForLate');
    }

    // Evidence is required - either file or text. In edit mode, an already-
    // attached evidence that is NOT being replaced already satisfies this.
    const evidenceSatisfiedByExisting = isEditMode && existingEvidence && !replaceEvidence;
    if (!evidenceSatisfiedByExisting) {
      if (evidenceType === 'file' && !selectedFile) {
        newErrors.evidence = 'يجب إرفاق ملف دليل أو إدخال نص';
      } else if (evidenceType === 'text' && !formData.evidenceText.trim()) {
        newErrors.evidence = 'يجب إدخال نص الدليل أو إرفاق ملف';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('validation.fillRequiredFields'));
      return;
    }

    setSubmitting(true);
    try {
      const numericValue = parseFloat(formData.value);
      let savedReadingId: number | null = null;

      if (isEditMode && readingId) {
        // Edit mode - update existing reading
        await kpiValuesApi.update(readingId, {
          value: numericValue,
          measurement_date: formData.measurementDate,
          notes: formData.notes?.trim() || undefined,
        } as any);
        savedReadingId = readingId;
      } else if (selectedPeriod === 'manual') {
        // Manual entry - use addReading API
        const reading = await kpisApi.addReading(kpiId, {
          value: formData.value,
          measurement_date: formData.measurementDate,
          notes: formData.notes?.trim() || undefined,
        });
        savedReadingId = reading.id;
      } else if (selectedScheduleId) {
        // Scheduled or late period - use markComplete API with notes
        const result = await schedulesApi.markComplete(
          selectedScheduleId,
          numericValue,
          formData.notes?.trim() || undefined
        );
        // The markComplete might return the reading data
        if (result && result.reading_id) {
          savedReadingId = result.reading_id;
        }
      } else {
        // Fallback to addReading if no schedule selected
        const reading = await kpisApi.addReading(kpiId, {
          value: formData.value,
          measurement_date: formData.measurementDate,
          notes: formData.notes?.trim() || undefined,
        });
        savedReadingId = reading.id;
      }

      // Upload evidence (required - file or text). In edit mode, skip when an
      // existing evidence is being kept (not replaced) so it isn't duplicated.
      if (savedReadingId && (!existingEvidence || replaceEvidence)) {
        try {
          if (evidenceType === 'file' && selectedFile) {
            const formDataEvidence = new FormData();
            formDataEvidence.append('related_type', 'kpi_value');
            formDataEvidence.append('related_id', String(savedReadingId));
            formDataEvidence.append('evidence_type', 'file');
            formDataEvidence.append('title', selectedFile.name || t('readings.readingEvidence'));

            // For web, create a blob from the file URI
            if (Platform.OS === 'web') {
              const response = await fetch(selectedFile.uri);
              const blob = await response.blob();
              formDataEvidence.append('file', blob, selectedFile.name);
            } else {
              formDataEvidence.append('file', {
                uri: selectedFile.uri,
                type: selectedFile.mimeType || 'application/octet-stream',
                name: selectedFile.name || 'file',
              } as any);
            }

            // Replacing an existing evidence updates that record in place;
            // otherwise create a new one.
            await (replaceEvidence && existingEvidence
              ? evidenceApi.update(existingEvidence.id, formDataEvidence)
              : evidenceApi.create(formDataEvidence));
          } else if (evidenceType === 'text' && formData.evidenceText.trim()) {
            const formDataEvidence = new FormData();
            formDataEvidence.append('related_type', 'kpi_value');
            formDataEvidence.append('related_id', String(savedReadingId));
            formDataEvidence.append('evidence_type', 'text');
            formDataEvidence.append('title', t('readings.evidenceNotes'));
            formDataEvidence.append('text_content', formData.evidenceText);

            // Replacing an existing evidence updates that record in place;
            // otherwise create a new one.
            await (replaceEvidence && existingEvidence
              ? evidenceApi.update(existingEvidence.id, formDataEvidence)
              : evidenceApi.create(formDataEvidence));
          }
        } catch (evidenceError: any) {
          console.error('Error uploading evidence:', evidenceError);
          const evidenceMsg = evidenceError.response?.data?.error
            || evidenceError.response?.data?.detail
            || 'فشل رفع الدليل. تم حفظ القراءة بنجاح.';
          showError(evidenceMsg);
          queryClient.invalidateQueries({ queryKey: queryKeys.kpis.all });
          navigation.goBack();
          return;
        }
      }

      showSuccess(isEditMode ? t('readings.editSuccess') : t('readings.addSuccess'));
      queryClient.invalidateQueries({ queryKey: queryKeys.kpis.all });
      navigation.goBack();
    } catch (error: any) {
      console.error('Error adding reading:', error);
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.value?.[0]
        || error.response?.data?.measurement_date?.[0]
        || error.response?.data?.non_field_errors?.[0]
        || t('readings.addFailed');
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const { requestCancel, confirmVisible, confirmDiscard, dismissConfirm } = useCancelConfirm({
    snapshot: { formData, selectedPeriod, selectedScheduleId, evidenceType, selectedFile },
    ready: !loading,
    onExit: handleCancel,
  });

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Clear evidence error when typing evidence text
    if (field === 'evidenceText' && errors.evidence) {
      setErrors(prev => ({ ...prev, evidence: '' }));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('ar-SA', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month}، ${year}`;
  };

  if (loading) {
    return (
      <PageContainer>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={tokens.colors.semantic.general["Main Color"]} />
        </View>
      </PageContainer>
    );
  }

  const getPolarityText = (polarity: string) => {
    switch (polarity) {
      case 'positive':
        return t('readings.polarityPositive');
      case 'negative':
        return t('readings.polarityNegative');
      default:
        return polarity;
    }
  };

  // Reading item colors - muted gray-blue to match Figma
  const getReadingColor = (_index: number) => {
    return '#94A3B8';
  };

  return (
    <PageContainer>
      <ScrollView style={styles.scrollView} contentContainerStyle={[styles.content, { padding: containerPadding }]}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('nav.home') },
            { label: t('kpis.kpiDetails'), route: 'KPIDetail', params: { id: kpiId } },
            { label: isEditMode ? t('readings.editReading') : t('readings.addReading') },
          ]}
        />

        {/* Page Title - KPI Name */}
        <View style={[styles.pageTitleRow, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          {/* <TouchableOpacity style={styles.viewAllButton}>
            <EyeIcon size={16} color={tokens.colors.raw.gray["500"]} />
            <Text style={styles.viewAllText}>{t('readings.viewAllReadings')}</Text>
          </TouchableOpacity> */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{translate(kpi?.name) || t('kpis.addNewReading')}</Text>
        </View>

        {/* Main Content - Two Column Layout */}
        <View style={[styles.mainContent, isSmallScreen && styles.mainContentSmall]}>
          {/* Left Column - Form */}
          <View style={[styles.leftColumn, isSmallScreen && styles.leftColumnSmall]}>
            <View style={[styles.formCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.formTitle, { color: themeColors.pageTitle }]}>{t('readings.enterReadingValue')}</Text>

              {/* Periods Section - Gray Background (hidden in edit mode) */}
              {!isEditMode && (
              <View style={[styles.periodsSection, { backgroundColor: themeColors.section }]}>
                {/* Late Periods */}
                {lateSchedules.length > 0 && (
                  <View style={styles.periodGroup}>
                    <Text style={[styles.periodGroupTitle, { color: themeColors.sectionTitle }]}>{t('readings.latePeriods')}</Text>
                    {lateSchedules.map((schedule) => {
                      const isSelected = selectedPeriod === 'late' && selectedScheduleId === schedule.id;
                      return (
                        <View key={schedule.id}>
                          <TouchableOpacity
                            style={styles.periodRadioRow}
                            onPress={() => {
                              setSelectedPeriod('late');
                              setSelectedScheduleId(schedule.id);
                            }}
                          >
                            <View style={styles.periodLabelContainer}>
                              <Text style={[styles.periodText, { color: themeColors.iconColor }, isSelected && styles.periodTextActive]}>
                                {translate(schedule.period_label)}
                              </Text>
                              <Text style={[styles.dueDateText, { color: tokens.colors.raw.red["500"] }]}>
                                {t('readings.dueDate')}: {formatShortDate(schedule.scheduled_date)}
                              </Text>
                            </View>
                            <View style={[styles.radio, { borderColor: themeColors.radioBorder }, isSelected && styles.radioActive]}>
                              {isSelected && <View style={styles.radioInner} />}
                            </View>
                          </TouchableOpacity>
                        </View>
                      );
                    })}
                  </View>
                )}

                {/* Scheduled Periods */}
                {pendingSchedules.length > 0 && (
                  <View style={styles.periodGroup}>
                    <Text style={[styles.periodGroupTitle, { color: themeColors.sectionTitle }]}>{t('readings.scheduledPeriods')}</Text>
                    {pendingSchedules.map((schedule) => (
                      <TouchableOpacity
                        key={schedule.id}
                        style={styles.periodRadioRow}
                        onPress={() => {
                          setSelectedPeriod('scheduled');
                          setSelectedScheduleId(schedule.id);
                        }}
                      >
                        <Text style={[styles.periodText, { color: themeColors.iconColor }, selectedPeriod === 'scheduled' && selectedScheduleId === schedule.id && styles.periodTextActive]}>
                          {translate(schedule.period_label)}
                        </Text>
                        <View style={[styles.radio, { borderColor: themeColors.radioBorder }, selectedPeriod === 'scheduled' && selectedScheduleId === schedule.id && styles.radioActive]}>
                          {selectedPeriod === 'scheduled' && selectedScheduleId === schedule.id && <View style={styles.radioInner} />}
                        </View>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}

                {/* Nothing due: every period is either already read or hasn't
                    closed yet. Reachable by design — say so instead of showing
                    an empty box. */}
                {lateSchedules.length === 0 && pendingSchedules.length === 0 && (
                  <View style={styles.periodGroup}>
                    <Text style={[styles.periodGroupTitle, { color: themeColors.sectionTitle }]}>
                      {t('readings.noPeriodsAvailable')}
                    </Text>
                    <Text style={[styles.periodText, { color: themeColors.iconColor }]}>
                      {t('readings.noPeriodsAvailableHint')}
                    </Text>
                  </View>
                )}

                {/* Manual free-date entry removed — a reading can only be added
                    for a scheduled/late period (auto-dated to the period end),
                    so it can't be entered before the period ends. */}
                {false && (
                <View style={styles.periodGroup}>
                  <Text style={[styles.periodGroupTitle, { color: themeColors.sectionTitle }]}>{t('readings.manualEntry')}</Text>
                  <TouchableOpacity
                    style={styles.periodRadioRow}
                    onPress={() => {
                      setSelectedPeriod('manual');
                      setSelectedScheduleId(null);
                    }}
                  >
                    <Text style={[styles.periodText, { color: themeColors.iconColor }, selectedPeriod === 'manual' && styles.periodTextActive]}>{t('readings.addReadingForSpecificDate')}</Text>
                    <View style={[styles.radio, { borderColor: themeColors.radioBorder }, selectedPeriod === 'manual' && styles.radioActive]}>
                      {selectedPeriod === 'manual' && <View style={styles.radioInner} />}
                    </View>
                  </TouchableOpacity>

                  {/* Date Input - shown when manual is selected */}
                  {selectedPeriod === 'manual' && (
                    <View style={styles.dateInputWrapper}>
                      <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: (periodAlreadyHasReading || isDateOutsideDueWindow) ? tokens.colors.raw.red["500"] : themeColors.inputBorder }]}>
                        <CalendarIcon size={18} color={themeColors.iconColor} />
                        {Platform.OS === 'web' ? (
                          <input
                            type="date"
                            value={formData.measurementDate}
                            min={dueWindowRange?.start}
                            max={dueWindowRange?.end}
                            onChange={(e: any) => updateField('measurementDate', e.target.value)}
                            style={{
                              flex: 1,
                              border: 'none',
                              outline: 'none',
                              backgroundColor: 'transparent',
                              fontSize: tokens.typography.paragraph["paragraph small font size"],
                              fontFamily: FONTS.regular,
                              color: isDark ? tokens.colors.raw.gray["200"] : tokens.colors.semantic.general.foreground,
                              textAlign: getTextAlign(isRTL),
                              direction: 'ltr' as const,
                              padding: '8px 0',
                              cursor: 'pointer',
                            }}
                          />
                        ) : (
                          <TextInput
                            style={[styles.dateInput, { color: themeColors.inputText }]}
                            value={formData.measurementDate}
                            onChangeText={(v) => updateField('measurementDate', v)}
                            placeholder="YYYY-MM-DD"
                            placeholderTextColor={themeColors.placeholder}
                            textAlign="right"
                          />
                        )}
                      </View>
                      {periodAlreadyHasReading && (
                        <View style={styles.periodWarning}>
                          <InfoIcon size={14} color={tokens.colors.raw.red["500"]} />
                          <Text style={styles.periodWarningText}>
                            توجد قراءة مسجلة بالفعل في {getPeriodLabel(kpi?.frequency || '')}. دورية المؤشر تسمح بقراءة واحدة فقط لكل فترة.
                          </Text>
                        </View>
                      )}
                      {!periodAlreadyHasReading && isDateOutsideDueWindow && dueWindowRange && (
                        <View style={styles.periodWarning}>
                          <InfoIcon size={14} color={tokens.colors.raw.red["500"]} />
                          <Text style={styles.periodWarningText}>
                            يجب أن يكون تاريخ القراءة ضمن فترة الاستحقاق ({formatShortDate(dueWindowRange.start)} - {formatShortDate(dueWindowRange.end)}).
                          </Text>
                        </View>
                      )}
                    </View>
                  )}
                </View>
                )}
              </View>
              )}

              {/* Date input for edit mode */}
              {isEditMode && (
                <View style={[styles.periodsSection, { backgroundColor: themeColors.section }]}>
                  <View style={styles.dateInputWrapper}>
                    <Text style={[styles.periodGroupTitle, { color: themeColors.sectionTitle }]}>{t('kpis.measurementDate')}</Text>
                    {/* Read-only: a reading stays tied to its period, so when
                        editing the measurement date is shown but not editable. */}
                    <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                      <CalendarIcon size={18} color={themeColors.iconColor} />
                      <Text style={[styles.dateInput, { color: themeColors.inputText, flex: 1, writingDirection: 'ltr' }]}>
                        {formData.measurementDate || '—'}
                      </Text>
                    </View>
                  </View>
                </View>
              )}

              {/* Value Inputs Row */}
              <View style={[styles.valueRow, isSmallScreen && styles.valueRowSmall]}>
                {/* Target Value - Left side, disabled */}
                <View style={styles.valueBox}>
                  <Text style={[styles.valueBoxLabel, { color: themeColors.label }]}>{t('kpis.targetValue')}</Text>
                  <View style={[styles.valueDisplayContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <Text style={[styles.valueUnitText, { color: themeColors.iconColor }]}>{translate(kpi?.unit) || t('kpis.number')}</Text>
                    <Text style={[styles.valueDisplayText, { color: themeColors.inputText }]}>{periodTargetValue || '--'}</Text>
                  </View>
                </View>

                {/* Achieved Value - Right side, active */}
                <View style={styles.valueBox}>
                  <View style={styles.labelRowRight}>
                    <Text style={styles.requiredStar}>*</Text>
                    <Text style={[styles.valueBoxLabel, { color: themeColors.label }]}>{t('readings.achievedValue')}</Text>
                  </View>
                  <View style={[styles.valueInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }, errors.value && styles.inputError]}>
                    <Text style={[styles.valueUnitText, { color: themeColors.iconColor }]}>{translate(kpi?.unit) || t('kpis.number')}</Text>
                    <TextInput
                      style={[styles.valueInput, { color: themeColors.inputText }]}
                      value={formData.value}
                      onChangeText={(v) => updateField('value', normalizeArabicNumbers(v))}
                      keyboardType="numeric"
                      textAlign="right"
                      placeholder="00"
                      placeholderTextColor={themeColors.placeholder}
                    />
                  </View>
                  {errors.value && <Text style={styles.errorText}>{errors.value}</Text>}
                </View>
              </View>

              {/* Info Message */}
              <View style={[styles.infoMessage, { backgroundColor: themeColors.infoMessage }]}>
                <Text style={[styles.infoText, { color: themeColors.inputText }]}>{t('readings.achievementCalculationInfo')}</Text>
                <InfoIcon size={20} color={themeColors.inputText} />
              </View>

              {/* Evidence Section - Gray Background */}
              <View style={[styles.evidenceSection, { backgroundColor: themeColors.section }, errors.evidence && { borderColor: tokens.colors.raw.red["500"], borderWidth: tokens.spacing.absolute["px"] }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.sectionLabel, { color: themeColors.sectionTitle }]}>{t('readings.addEvidence')}</Text>
                </View>

                {isEditMode && existingEvidence && !replaceEvidence ? (
                  <View style={styles.uploadSection}>
                    <Text style={[styles.evidenceRadioLabel, { color: themeColors.sectionTitle }]}>{t('readings.currentEvidence')}</Text>
                    {existingEvidence.evidence_type === 'file' && existingEvidence.file ? (
                      // Show the uploaded file as-is — an openable link, like the KPI detail screen.
                      <TouchableOpacity
                        style={[styles.fileNameContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}
                        onPress={() => {
                          const f = String(existingEvidence.file);
                          Linking.openURL(f.startsWith('http') ? f : `${API_BASE_HOST}${f}`);
                        }}
                      >
                        <Text style={[styles.fileNameText, { color: themeColors.sectionTitle, textDecorationLine: 'underline' }]}>
                          {translate(existingEvidence.title) || t('kpis.viewFile')}
                        </Text>
                      </TouchableOpacity>
                    ) : (
                      <View style={[styles.fileNameContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                        <Text style={[styles.fileNameText, { color: themeColors.iconColor }]}>
                          {existingEvidence.evidence_type === 'text'
                            ? (translate(existingEvidence.text_content) || t('kpis.text'))
                            : (existingEvidence.title || t('readings.readingEvidence'))}
                        </Text>
                      </View>
                    )}
                    <TouchableOpacity
                      style={[styles.uploadButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}
                      onPress={() => { setReplaceEvidence(true); setEvidenceType('file'); setSelectedFile(null); updateField('evidenceText', ''); }}
                    >
                      <Text style={[styles.uploadButtonText, { color: themeColors.sectionTitle }]}>{t('readings.replaceEvidence')}</Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                <>
                {/* File Upload Option */}
                <TouchableOpacity
                  style={styles.evidenceRadioRow}
                  onPress={() => { setEvidenceType('file'); if (errors.evidence) setErrors(prev => ({ ...prev, evidence: '' })); }}
                >
                  <Text style={[styles.evidenceRadioLabel, { color: themeColors.sectionTitle }]}>{t('readings.attachEvidenceFile')}</Text>
                  <View style={[styles.radio, { borderColor: themeColors.radioBorder }, evidenceType === 'file' && styles.radioActive]}>
                    {evidenceType === 'file' && <View style={styles.radioInner} />}
                  </View>
                </TouchableOpacity>

                {evidenceType === 'file' && (
                  <View style={styles.uploadSection}>
                    <View style={styles.fileInputRow}>
                      <TouchableOpacity style={[styles.uploadButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]} onPress={handleFilePick}>
                        <Text style={[styles.uploadButtonText, { color: themeColors.sectionTitle }]}>{t('readings.selectFile')}</Text>
                      </TouchableOpacity>
                      <View style={[styles.fileNameContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                        <Text style={[styles.fileNameText, { color: themeColors.iconColor }]}>{selectedFile?.name || t('readings.noFileSelected')}</Text>
                      </View>
                    </View>
                    <View style={styles.uploadInfo}>
                      <Text style={styles.uploadInfoText}>{t('readings.allowedFileTypes')}</Text>
                      <InfoIcon size={14} color={themeColors.placeholder} />
                    </View>
                  </View>
                )}

                {/* Text Input Option */}
                <TouchableOpacity
                  style={styles.evidenceRadioRow}
                  onPress={() => { setEvidenceType('text'); if (errors.evidence) setErrors(prev => ({ ...prev, evidence: '' })); }}
                >
                  <Text style={[styles.evidenceRadioLabel, { color: themeColors.sectionTitle }]}>{t('readings.enterText')}</Text>
                  <View style={[styles.radio, { borderColor: themeColors.radioBorder }, evidenceType === 'text' && styles.radioActive]}>
                    {evidenceType === 'text' && <View style={styles.radioInner} />}
                  </View>
                </TouchableOpacity>

                {evidenceType === 'text' && (
                  <View style={styles.evidenceTextSection}>
                    <TextInput
                      style={[styles.evidenceTextInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                      value={formData.evidenceText}
                      onChangeText={(v) => updateField('evidenceText', v)}
                      placeholder=""
                      placeholderTextColor={themeColors.placeholder}
                      textAlign="right"
                      multiline
                      numberOfLines={3}
                    />
                    <View style={styles.uploadInfo}>
                      <Text style={styles.uploadInfoText}>{t('readings.textEvidenceInfo')}</Text>
                      <InfoIcon size={14} color={themeColors.placeholder} />
                    </View>
                  </View>
                )}
                </>
                )}
                {errors.evidence && <Text style={styles.errorText}>{errors.evidence}</Text>}
              </View>

              {/* Notes Field */}
              <View style={styles.notesField}>
                <View style={styles.labelRow}>
                  {isLateReading && <Text style={styles.requiredStar}>*</Text>}
                  <Text style={[styles.sectionLabel, { color: themeColors.sectionTitle }]}>{t('common.notes')}</Text>
                </View>
                {Platform.OS === 'web' ? (
                  <textarea
                    value={formData.notes}
                    onChange={(e: any) => updateField('notes', e.target.value)}
                    style={{
                      backgroundColor: isDark ? colors.card : tokens.colors.raw.white,
                      borderWidth: tokens.spacing.absolute["px"],
                      borderColor: errors.notes ? tokens.colors.raw.red["500"] : (isDark ? colors.border : tokens.colors.raw.gray["200"]),
                      borderStyle: 'solid',
                      borderRadius: tokens.radii.absolute["radius-8"],
                      padding: tokens.spacing.absolute["3"],
                      minHeight: 80,
                      fontSize: tokens.typography.paragraph["paragraph small font size"],
                      fontFamily: FONTS.regular,
                      color: isDark ? tokens.colors.raw.gray["200"] : tokens.colors.semantic.general.foreground,
                      textAlign: getTextAlign(isRTL),
                      direction: 'rtl' as const,
                      resize: 'vertical' as const,
                      outline: 'none',
                    }}
                  />
                ) : (
                  <TextInput
                    style={[styles.notesInput, { backgroundColor: themeColors.input, borderColor: errors.notes ? tokens.colors.raw.red["500"] : themeColors.inputBorder, color: themeColors.inputText }]}
                    value={formData.notes}
                    onChangeText={(v) => updateField('notes', v)}
                    placeholder=""
                    placeholderTextColor={themeColors.placeholder}
                    textAlign="right"
                    multiline
                    numberOfLines={3}
                  />
                )}
                {errors.notes && <Text style={styles.errorText}>{errors.notes}</Text>}
                <View style={styles.notesInfo}>
                  <Text style={styles.notesInfoText}>
                    {isLateReading ? t('readings.lateReadingNotesRequired') : t('readings.notesOptional')}
                  </Text>
                  <InfoIcon size={14} color={themeColors.placeholder} />
                </View>
              </View>

              {/* Action Buttons */}
              <View style={styles.actionButtons}>
                <Button
                  variant="primary"
                  onPress={handleSave}
                  loading={submitting}
                  disabled={periodAlreadyHasReading}
                >
                  {t('common.save')}
                </Button>
                <Button variant="secondary" onPress={requestCancel} disabled={submitting}>
                  {t('common.cancel')}
                </Button>
              </View>
            </View>
          </View>

          {/* Right Column - KPI Info */}
          <View style={[styles.rightColumn, isSmallScreen && styles.rightColumnSmall]}>
            {/* KPI Information Card */}
            <View style={[styles.infoCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.infoCardTitle, { color: themeColors.inputText }]}>{t('readings.kpiInformation')}</Text>

              <View style={styles.infoRow}>
                <Text style={[styles.infoValue, { color: themeColors.inputText }]}>{t('kpis.strategic')}</Text>
                <View style={[styles.infoLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                  <Text style={[styles.infoLabelText, { color: themeColors.inputText }]}>{t('kpis.type')}</Text>
                </View>
              </View>

              <View style={styles.infoRow}>
                <Text style={[styles.infoValue, { color: themeColors.inputText }]}>{periodTargetValue || '--'} {translate(kpi?.unit) || t('kpis.number')}</Text>
                <View style={[styles.infoLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                  <Text style={[styles.infoLabelText, { color: themeColors.inputText }]}>{t('kpis.target')}</Text>
                </View>
              </View>

              {kpi?.worst_acceptable_value != null && (
                <View style={styles.infoRow}>
                  <Text style={[styles.infoValue, { color: themeColors.inputText }]}>{kpi.worst_acceptable_value} {translate(kpi?.unit) || t('kpis.number')}</Text>
                  <View style={[styles.infoLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                    <Text style={[styles.infoLabelText, { color: themeColors.inputText }]}>{t('kpis.worstAcceptableValue')}</Text>
                  </View>
                </View>
              )}

              <View style={styles.infoRow}>
                <Text style={[styles.infoValue, { color: themeColors.inputText }]}>{kpi?.polarity ? getPolarityText(kpi.polarity) : t('readings.polarityPositive')}</Text>
                <View style={[styles.infoLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                  <Text style={[styles.infoLabelText, { color: themeColors.inputText }]}>{t('kpis.polarity')}</Text>
                </View>
              </View>

              <View style={styles.infoRow}>
                <Text style={[styles.infoValue, { color: themeColors.inputText }]}>{kpi?.measurement_formula || '—'}</Text>
                <View style={[styles.infoLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                  <Text style={[styles.infoLabelText, { color: themeColors.inputText }]}>{t('kpis.measurementFormula')}</Text>
                </View>
              </View>

              <View style={[styles.infoRow, styles.infoRowLast]}>
                <Text style={[styles.infoValue, { color: themeColors.inputText }]}>{translate(kpi?.owner_name) || '—'}</Text>
                <View style={[styles.infoLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                  <Text style={[styles.infoLabelText, { color: themeColors.inputText }]}>{t('kpis.responsible')}</Text>
                </View>
              </View>
            </View>

            {/* Current Year Progress Card */}
            <View style={[styles.progressCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.progressCardTitle, { color: themeColors.inputText }]}>{t('readings.currentYearProgress')}</Text>
              <View style={styles.progressContent}>
                <View style={styles.progressTextContainer}>
                  <View style={styles.progressRow}>
                    <Text style={[styles.progressValue, { color: themeColors.inputText }]}>{currentYearReadingsCount}/{getExpectedReadingsPerYear(kpi?.frequency)}</Text>
                    <View style={[styles.progressLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                      <Text style={[styles.progressLabelText, { color: themeColors.inputText }]}>{t('readings.readings')}</Text>
                    </View>
                  </View>
                  <View style={styles.progressRow}>
                    <Text style={[styles.progressValue, { color: themeColors.inputText }]}>{kpi?.commitment_rate != null ? `${kpi.commitment_rate} %` : '—'}</Text>
                    <View style={[styles.progressLabelBadge, { backgroundColor: themeColors.badgeBg }]}>
                      <Text style={[styles.progressLabelText, { color: themeColors.inputText }]}>{t('readings.percentage')}</Text>
                    </View>
                  </View>
                </View>
                <ProgressCircle
                  percentage={kpi?.commitment_rate ?? 0}
                  readings={currentYearReadingsCount}
                  total={getExpectedReadingsPerYear(kpi?.frequency)}
                />
              </View>
            </View>

            {/* Recent Readings Card */}
            <View style={[styles.readingsCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.readingsTitle, { color: themeColors.pageTitle }]}>{t('readings.recentReadings')}</Text>
              {recentReadings.length > 0 ? (
                recentReadings.map((reading, index) => (
                  <View key={reading.id || index} style={[styles.readingItem, { backgroundColor: themeColors.readingBg }]}>
                    <View style={[styles.readingColorBar, { backgroundColor: getReadingColor(index) }]} />
                    <View style={styles.readingContent}>
                      <Text style={[styles.readingValue, { color: themeColors.pageTitle }]}>{reading.value} {translate(kpi?.unit) || t('kpis.number')}</Text>
                      <Text style={[styles.readingDate, { color: themeColors.iconColor }]}>{formatShortDate(reading.measurement_date)}</Text>
                    </View>
                  </View>
                ))
              ) : (
                <View style={{ alignItems: 'center', paddingVertical: tokens.spacing.absolute["4"] }}>
                  <Text style={{ color: themeColors.placeholder, fontSize: tokens.typography.paragraph["paragraph small font size"] }}>{t('readings.noPreviousReadings')}</Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </ScrollView>
      <CancelConfirmModal
        visible={confirmVisible}
        onConfirm={confirmDiscard}
        onClose={dismissConfirm}
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: tokens.spacing.absolute["6"],
  },
  contentSmall: {
    padding: tokens.spacing.absolute["3"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  breadcrumbSmall: {
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["1"],
  },
  breadcrumbLink: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general["Main Color"],
  },
  // Page Title Row
  pageTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["6"],
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
  },
  pageTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["800"],
    textAlign: getTextAlign(isRTL),
    flex: 1,
  },
  viewAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
  },
  viewAllText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  // Main Content
  mainContent: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["6"],
  },
  mainContentSmall: {
    flexDirection: 'column-reverse',
    gap: tokens.spacing.absolute["4"],
  },
  leftColumn: {
    flex: 2,
  },
  leftColumnSmall: {
    flex: 1,
  },
  rightColumn: {
    flex: 1,
    gap: tokens.spacing.absolute["3"],
  },
  rightColumnSmall: {
    flex: 1,
  },
  // Form Card
  formCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
  },
  formTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["800"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["5"],
  },
  // Periods Section
  periodsSection: {
    backgroundColor: tokens.colors.raw.gray["100"],
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["5"],
  },
  periodGroup: {
    marginBottom: tokens.spacing.absolute["4"],
  },
  periodGroupTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
  },
  periodRadioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    minHeight: 44,
    paddingVertical: tokens.spacing.absolute["1"],
  },
  periodLabelContainer: {
    flex: 1,
    alignItems: 'flex-end',
  },
  periodText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  periodTextActive: {
    color: tokens.colors.semantic.general["Main Color"],
  },
  dueDateText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
    marginTop: tokens.spacing.absolute["0_5"],
  },
  // Radio
  radio: {
    width: 20,
    height: 20,
    borderRadius: tokens.radii.absolute["radius-10"],
    borderWidth: 2,
    borderColor: tokens.colors.raw.gray["300"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioActive: {
    borderColor: tokens.colors.raw.gray["800"],
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute["radius-5"],
    backgroundColor: tokens.colors.raw.gray["800"],
  },
  // Date Input
  dateInputWrapper: {
    marginTop: tokens.spacing.absolute["3"],
  },
  periodWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    marginTop: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["1"],
  },
  periodWarningText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
    textAlign: getTextAlign(isRTL),
    flex: 1,
  },
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2"],
  },
  dateInput: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
  },
  // Value Row
  valueRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["6"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  valueRowSmall: {
    flexDirection: 'column',
    gap: tokens.spacing.absolute["4"],
  },
  valueBox: {
    flex: 1,
  },
  valueBoxLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["1_5"],
  },
  labelRowRight: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacing.absolute["0"],
    marginBottom: tokens.spacing.absolute["1_5"],
  },
  valueDisplayContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    minHeight: 44,
    opacity: 0.5,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  valueDisplayText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  valueUnitText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  valueInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    minHeight: 44,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  valueInput: {
    flex: 1,
    paddingVertical: 7.5,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
  },
  inputError: {
    borderColor: tokens.colors.raw.red["500"],
  },
  // Labels
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  sectionLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
  },
  requiredStar: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#D62728',
  },
  // Info Message
  infoMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    marginBottom: tokens.spacing.absolute["5"],
  },
  infoText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Evidence Section
  evidenceSection: {
    backgroundColor: tokens.colors.raw.gray["100"],
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["5"],
  },
  evidenceRadioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["3"],
    minHeight: 44,
  },
  evidenceRadioLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["700"],
  },
  uploadSection: {
    marginBottom: tokens.spacing.absolute["4"],
    paddingEnd: 28,
  },
  fileInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  fileNameContainer: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    minHeight: 44,
    justifyContent: 'center',
  },
  fileNameText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
  },
  uploadButton: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    minHeight: 44,
    justifyContent: 'center',
  },
  uploadButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
  },
  uploadInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1_5"],
  },
  uploadInfoText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
  },
  evidenceTextSection: {
    paddingEnd: 28,
    marginBottom: tokens.spacing.absolute["3"],
  },
  evidenceTextInput: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    minHeight: 80,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
    textAlignVertical: 'top',
    marginBottom: tokens.spacing.absolute["2"],
  },
  // Notes Field
  notesField: {
    marginBottom: tokens.spacing.absolute["6"],
  },
  notesInput: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    minHeight: 80,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
    textAlignVertical: 'top',
    marginBottom: tokens.spacing.absolute["2"],
  },
  notesInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1_5"],
  },
  notesInfoText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
  },
  // Action Buttons
  actionButtons: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
    direction: 'ltr' as any,
    justifyContent: 'flex-start',
  },
  // Error
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
    textAlign: getTextAlign(isRTL),
    marginTop: tokens.spacing.absolute["1"],
  },
  // Info Card
  infoCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
    elevation: 1,
  },
  infoCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  infoRowLast: {
    marginBottom: tokens.spacing.absolute["0"],
  },
  infoValue: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  infoLabelBadge: {
    backgroundColor: 'rgba(51, 65, 85, 0.1)',
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["1"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    width: 80,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  infoLabelText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Progress Card
  progressCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
    elevation: 1,
  },
  progressCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },
  progressContent: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  progressTextContainer: {
    flex: 1,
    gap: tokens.spacing.absolute["2"],
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2_5"],
  },
  progressLabelBadge: {
    backgroundColor: 'rgba(51, 65, 85, 0.1)',
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["1"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    height: 20,
    width: 80,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  progressLabelText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  progressValue: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
  },
  // Readings Card
  readingsCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
  },
  readingsTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["800"],
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  readingItem: {
    flexDirection: 'row',
    alignItems: 'stretch',
    backgroundColor: tokens.colors.raw.gray["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
    marginBottom: tokens.spacing.absolute["2"],
    overflow: 'hidden',
  },
  readingColorBar: {
    width: 4,
  },
  readingContent: {
    flex: 1,
    padding: tokens.spacing.absolute["3"],
    alignItems: 'flex-end',
  },
  readingValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.raw.gray["800"],
  },
  readingDate: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    marginTop: tokens.spacing.absolute["0_5"],
  },
  noReadingsText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
    textAlign: 'center',
    paddingVertical: tokens.spacing.absolute["5"],
  },
});
